import { useState } from 'react';
import { PROFILE_EMOJIS, type ViewId, type WithdrawRecord } from '../state';
import type { Role } from '../lib/api';
import { Icon } from '../components/Icon';

type Props = {
    onNavigate: (id: ViewId) => void;
    onOpenWithdraw: () => void;
    onOpenEmojiPicker: () => void;
    avatar: string;
    setAvatar: (e: string) => void;
    name: string;
    username: string;
    userId: number;
    joinedAt: string;
    withdrawn: number;
    earnedBonuses: number;
    isAnon: boolean;
    setIsAnon: (v: boolean) => void;
    history: WithdrawRecord[];
    /** Shown only if the server reports a privileged role for this user. */
    adminEntry?: { role: Role; onOpen: () => void } | null;
};

/**
 * Profile screen. The avatar is an emoji picker: tap the avatar to open the
 * full grid, or use the side arrows to step through PROFILE_EMOJIS in
 * either direction. This matches the spec ("сразу встроить пул эмодзи и
 * сделать 2 стрелочки").
 */
export function ProfileView({
    onNavigate,
    onOpenWithdraw,
    onOpenEmojiPicker,
    avatar,
    setAvatar,
    name,
    username,
    userId,
    joinedAt,
    withdrawn,
    earnedBonuses,
    isAnon,
    setIsAnon,
    history,
    adminEntry,
}: Props) {
    const [stepLock, setStepLock] = useState(false);

    const step = (dir: 1 | -1) => {
        if (stepLock) return;
        setStepLock(true);
        const idx = PROFILE_EMOJIS.indexOf(avatar);
        const next = (idx + dir + PROFILE_EMOJIS.length) % PROFILE_EMOJIS.length;
        const picked = PROFILE_EMOJIS[next] ?? PROFILE_EMOJIS[0] ?? avatar;
        setAvatar(picked);
        window.setTimeout(() => setStepLock(false), 120);
    };

    return (
        <div className="view">
            <div className="profile-header">
                <div className="avatar-row">
                    <button
                        type="button"
                        className="avatar-arrow"
                        aria-label="Предыдущий аватар"
                        onClick={() => step(-1)}
                    >
                        ‹
                    </button>
                    <div
                        className="avatar-container"
                        role="button"
                        aria-label="Сменить аватар"
                        onClick={onOpenEmojiPicker}
                    >
                        {avatar}
                    </div>
                    <button
                        type="button"
                        className="avatar-arrow"
                        aria-label="Следующий аватар"
                        onClick={() => step(1)}
                    >
                        ›
                    </button>
                </div>
                <div className="profile-name">{name}</div>
                <div className="profile-username">@{username}</div>
            </div>

            <div className="info-card">
                <div className="info-row">
                    <span className="info-label">ID</span>
                    <span className="info-value">{userId}</span>
                </div>
                <div className="info-row">
                    <span className="info-label">В боте с</span>
                    <span className="info-value">{joinedAt}</span>
                </div>
                <div className="info-row">
                    <span className="info-label">Выведено BSP</span>
                    <span className="info-value text-green">{withdrawn.toLocaleString('ru-RU')}</span>
                </div>
                <div className="info-row">
                    <span className="info-label">Заработано на бонусах</span>
                    <span className="info-value text-accent">
                        {earnedBonuses.toLocaleString('ru-RU')}
                    </span>
                </div>
            </div>

            <div className="info-card">
                <label className="checkbox-row">
                    <div>
                        <div className="fw-900 f-12" style={{ color: '#fff' }}>
                            Анонимный режим
                        </div>
                        <div className="text-muted" style={{ fontSize: 10, marginTop: 2 }}>
                            Ваш @username будет скрыт.
                        </div>
                    </div>
                    <input
                        type="checkbox"
                        checked={isAnon}
                        onChange={(e) => setIsAnon(e.target.checked)}
                    />
                </label>
            </div>

            <button
                type="button"
                className="action-btn btn-gold"
                style={{ padding: 14, fontSize: 12 }}
                onClick={onOpenWithdraw}
            >
                ПРОДАТЬ BSP
            </button>

            {adminEntry && (
                <button
                    type="button"
                    className="action-btn btn-outline-gold"
                    style={{ padding: 12, fontSize: 11, letterSpacing: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                    onClick={adminEntry.onOpen}
                >
                    <Icon name="shield" size={16} color="var(--accent)" />
                    {adminEntry.role === 'root' ? 'ROOT' : adminEntry.role === 'admin' ? 'АДМИН' : 'МОДЕРАТОР'}-ПАНЕЛЬ
                </button>
            )}

            {history.length > 0 && (
                <div className="info-card">
                    <h3 className="text-center text-muted fw-900 f-12">МОИ ПРОДАЖИ</h3>
                    {history.slice(0, 5).map((w) => (
                        <div className="top-row" key={w.id}>
                            <span className="top-name">
                                {methodLabel(w.method)} · {w.target.slice(0, 16)}
                            </span>
                            <span
                                className="top-val"
                                style={{ color: statusColor(w.status) }}
                            >
                                {w.amount} BSP
                            </span>
                        </div>
                    ))}
                </div>
            )}

            <button type="button" className="back-btn muted" onClick={() => onNavigate('menu')}>
                НАЗАД В МЕНЮ
            </button>
        </div>
    );
}

function methodLabel(m: WithdrawRecord['method']) {
    switch (m) {
        case 'stars':     return '⭐ Stars';
        case 'gift':      return '🎁 Подарки';
        case 'ton':       return '💎 TON';
        case 'cryptobot': return '🤖 CryptoBot';
    }
}
function statusColor(s: WithdrawRecord['status']) {
    if (s === 'sent') return 'var(--green)';
    if (s === 'failed') return 'var(--red)';
    return 'var(--accent)';
}
